import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  View
} from 'react-native';
import styled from 'styled-components/native';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import Link from '../../components/Link';
import Spacer from '../../components/Spacer';
import { H2, H5 } from '../../components/Typography';
import { IntroParamList } from '../../navigation/IntroNavigator';
import { MaterialIcons } from '@expo/vector-icons';
import Colors from '../../constants/Colors';
import { useVerifyEmail } from '../../hooks/user/useVerifyEmail';
import AlertBox from '../../components/AlertBox';
import * as LocalAuthentication from 'expo-local-authentication';
import EnableBiometricsModal from './modals/EnableBiometricsModal';
import api from '../../api/api';
import { useUserContext } from '../../context/UserContext';
import accessibilityTags from '../../../accessibilityTags';

const StyledView = styled.KeyboardAvoidingView<{ isDesktop: boolean }>`
  flex: 1;
  align-items: center;
  ${p => (p.isDesktop ? 'justify-content: center;' : '')}
`;

const StyledImageBackground = styled.ImageBackground`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StyledEmailVerificationView = styled.ScrollView<{ isDesktop: boolean }>`
  width: 100%;
  height: 100%;
  padding: ${p => (p.isDesktop ? 0 : 87)}px 24px 40px 24px;
  ${p => (p.isDesktop ? 'max-height: 1080px;' : '')}
`;

const StyledLink = styled(Link)`
  text-align: center;
`;

const StyledFormView = styled.View<{ isDesktop: boolean }>`
  flex: 1;
  justify-content: space-between;
  align-items: center;
  align-self: center;
  max-width: 438px;
  width: 100%;
`;

const StyledHeaderView = styled.View<{ isDesktop: boolean }>`
  align-items: center;
  align-self: center;
  max-width: 438px;
  width: 100%;
  margin-top: ${p => (p.isDesktop ? '100' : '0')}px;
`;

const SignUpEmailVerificationScreen: React.FC = () => {
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const { updateUserSession } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route =
    useRoute<RouteProp<IntroParamList, 'SignUpEmailVerificationScreen'>>();

  const [showBiometric, setShowBiometric] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  const verifyEnrolledBiometric = async () => {
    const savedBiometrics = await LocalAuthentication.isEnrolledAsync();
    setHasBiometric(savedBiometrics);
  };

  useEffect(() => {
    if (Platform.OS !== 'web') verifyEnrolledBiometric();
  }, []);

  const { jwtToken, error, verifyEmail } = useVerifyEmail();
  useEffect(() => {
    if (route.params.token) {
      verifyEmail({ token: route.params.token });
    }
  }, [route.params.token, verifyEmail]);

  const navigateToWelcomeScreen = useCallback(() => {
    navigation.replace('WelcomeNavigator', {
      screen: 'Welcome'
    });
  }, [navigation]);

  // after email verification redirect to Welcome Screen
  useEffect(() => {
    (async () => {
      if (jwtToken?.accessToken) {
        await api.setAccessToken(jwtToken.accessToken);
        await updateUserSession();
        if (!hasBiometric) {
          navigateToWelcomeScreen();
        } else {
          setShowBiometric(true);
        }
      }
    })();
  }, [
    hasBiometric,
    jwtToken,
    navigateToWelcomeScreen,
    navigation,
    updateUserSession
  ]);

  return (
    <StyledView
      behavior={Platform.select({ ios: 'padding' })}
      isDesktop={isDesktop}
    >
      <StyledImageBackground
        resizeMode='stretch'
        source={require('../../assets/intro/background.png')}
      />
      <StyledEmailVerificationView
        isDesktop={isDesktop}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <StyledHeaderView isDesktop={isDesktop}>
          <H2 color='dark' padding={2}>
            {route.params.token ? 'Verifying Email' : 'Email Verification'}
          </H2>
          <Spacer size={2} />
          <MaterialIcons
            name='mail-outline'
            size={128}
            color={Colors.chloeBlack}
          />
          <Spacer size={2} />
          {route.params.token ? (
            error ? (
              <>
                <AlertBox severity='error'>
                  {error.message === 'Unauthorized'
                    ? 'There was an error verifying your account. Use the forgot password feature to resend a verification link.'
                    : error.message}
                </AlertBox>
                <Spacer size={5} />
                <StyledLink
                  inverse
                  onPress={() => {
                    navigation.navigate('ForgotPasswordScreen');
                  }}
                >
                  Forgot Password
                </StyledLink>
              </>
            ) : (
              <ActivityIndicator color={Colors.primary} size='large' />
            )
          ) : (
            <H5 accessibilityLabel={accessibilityTags.emailVerificationLabelID} testID={accessibilityTags.emailVerificationLabelID} color='dark' center>
              An email verification was sent to{' '}
              <H5>{route.params.email || ''}</H5>. Please open the email and
              click the verification link.
            </H5>
          )}
        </StyledHeaderView>
        <Spacer size={5} />
        {route.params.token ? (
          <></>
        ) : (
          <StyledFormView isDesktop={isDesktop}>
            <View style={{ width: '100%' }}>
              <Spacer size={5} />
              <StyledLink
                inverse
                onPress={() => {
                  navigation.navigate('LoginScreen');
                }}
              >
                Return To Sign In
              </StyledLink>
            </View>
          </StyledFormView>
        )}
      </StyledEmailVerificationView>
      {showBiometric && (
        <EnableBiometricsModal
          onRequestClose={() => navigateToWelcomeScreen()}
        />
      )}
    </StyledView>
  );
};

export default SignUpEmailVerificationScreen;
